import { EmissionValueAnsweredUnit } from '@api-client/api'
import { InfoIcon } from '@assets/icons'
import { Dropdown } from '@components/Dropdown'
import { UnitTooltip } from '@components/Tooltips'
import { useDropdownTranslations } from '@hooks/useDropdownTranslations'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
	name: string
	required?: boolean
	dataTestId?: string
}

const FormUnitSelector: React.FC<Props> = ({ name, required, dataTestId }) => {
	const { t } = useTranslation()
	const { control } = useFormContext()
	const { translateDropdownOptions } = useDropdownTranslations()

	const unitOptions = useMemo(
		() =>
			translateDropdownOptions('emissions.unit.name', {
				// Dimensionless shares the kgCO2eq/item label with KGCO2_EQ_ITEM, so only offer the latter.
				context: Object.values(EmissionValueAnsweredUnit).filter(
					f =>
						f !== EmissionValueAnsweredUnit.OTHER &&
						f !== EmissionValueAnsweredUnit.KGCO2_EQ_DIMENSIONLESS
				),
			}),
		[translateDropdownOptions]
	)

	const renderField = useCallback(
		({
			field,
			fieldState: { error },
		}: {
			field: {
				value: EmissionValueAnsweredUnit
				onChange: (value: unknown) => void
			}
			fieldState: { error?: { type?: string } }
		}) => (
			<Dropdown
				value={field.value}
				handleChange={field.onChange}
				options={unitOptions}
				fullWidth
				variant="outlined"
				fieldError={error?.type}
				inputProps={{ 'data-testid': dataTestId }}
			/>
		),
		[unitOptions, dataTestId]
	)

	return (
		<Stack gap={1}>
			<Stack direction="row" gap={1} alignItems="center">
				<Typography variant="body2">
					{t('formField.emissionsUnitLabelRequired')}
				</Typography>
				<Tooltip title={<UnitTooltip />}>
					<IconButton size="small">
						<InfoIcon />
					</IconButton>
				</Tooltip>
			</Stack>
			<Controller
				name={name}
				control={control}
				rules={{ required }}
				defaultValue={EmissionValueAnsweredUnit.KGCO2_EQ_KG}
				render={renderField}
			/>
		</Stack>
	)
}

export { FormUnitSelector }
